import isEmpty from 'lodash/isEmpty'
import { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import useLocalStorageState from 'use-local-storage-state'
import { useReactTable, flexRender, getCoreRowModel } from '@tanstack/react-table'
import { useQuery, useQueryClient, useMutation, useIsMutating } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import Flag from 'react-flagkit'
import { getWholesaleProductsAPI, getB2BSupplierProductsAPI, updateWholesaleEntryAPI } from '../lib/api/api'
import { useWholesaleAllFilesQuery, useWholesaleExportListMutation } from '../services/wholesale'
import { useFiltersQuery, useFilterStore } from '../services/filters'
import { toast } from 'react-toastify'

import './ProductTable.css'
import { currencyFormatter } from '../utlis/utlis'
import { buildSupplierProductUrl } from '../lib/supplierLinks'
import Tippy from '@tippyjs/react'
import 'tippy.js/dist/tippy.css'
import classNames from 'classnames'
import Subheader from './Subheader'
import CommonModal from './CommonModal'
import ProductFavorite from './ProductFavorite'
import FavoriteStar from './FavoriteStar'
import ProductGrid, { EditablePackageQuantityCell, EditableBuypriceCell } from './ProductGrid'
import { LayoutHeaderSlot } from '../layout/Layout'
import Button from './Button'
import RegionManager from './RegionManager'
import AICheckButton from './AICheckButton'
import { SeasonalityCell, VelocityPriceCell, computeVelocityPriceRange } from './ProductGridSeasonality'
import AIAnalysisTag from './AIAnalysisTag'
import MismatchIcon from './MismatchIcon'

import Filters from './Filters'
import { ReactComponent as SortIcon } from '../icons/arrow.svg'
import { ReactComponent as RefreshIcon } from '../icons/refresh.svg'
import { useDebouncedCallback } from 'use-debounce'
import Pill from './Pill'
import PaginationBar from './PaginationBar'
import ColumnChooserPopover from './ColumnChooserPopover'
import { ReactComponent as ChartIcon } from '../icons/chart.svg'
import { ReactComponent as ImgIcon } from '../icons/imgicon.svg'
import { ReactComponent as ExportIcon } from '../icons/export.svg'
import a1Img from '../icons/a1.png'
import { ReactComponent as KeepaIcon } from '../icons/keepa.svg'
import { resolveRegionAIAnalysis } from '../utlis/aiAnalysis'
import useColumnSettingsSync, { useColumnSettingsLoader } from '../hooks/useColumnSettingsSync'
import { scrollToTopFrom } from './scrollToTop'


const application = 'wholesale_wizard'
const ALLOWED_PAGE_SIZES = [20, 50, 100]
const TABLE_SETTINGS_MODULE = 'wholesale_table'
const GRID_SETTINGS_MODULE = 'wholesale_grid'
const TABLE_STORAGE_KEY_PREFIX = 'wholesale_table'
const GRID_STORAGE_KEY_PREFIX = 'wholesale_grid'

const RIGHT_PINNED_COLUMNS = 0

const SORT_COLUMNS = [
  'margin',
  'roi',
  'profit',
  'margin30',
  'roi30',
  'profit30',
  'sellprice',
  'sellprice30',
  'buyprice',
  'salesrank',
  'percent_bsr',
  'confirmed_sales',
  'estimated_sales',
  'salesdrops',
  'variations',
  'offers',
  'brand',
]

const GRID_COLUMN_CHOOSER_APP = GRID_STORAGE_KEY_PREFIX
const SUPPORTED_GRID_REGIONS = ['de', 'fr', 'it', 'es']
const MULTI_REGION_MEMBERSHIPS = new Set(['EDGE PRO+', 'EDGE PRO', 'SCAN PRO+'])

const GRID_COLUMNS = [
  ['amz_img', 'Amazon img'],
  ['actions', 'Befehle'],
  ['keepa_chart_dk', 'Chart'],
  ['flags', 'Flaggen'],
  ['margin', 'Marge'],
  ['packageQuantity', 'Einheiten'],
  ['roi', 'ROI'],
  ['profit', 'Profit'],
  ['salesrank', 'BSR'],
  ['percent_bsr', 'BSR%'],
  ['asin', 'Asin'],
  ['product_id_list', 'Product ID'],
  ['name', 'Title'],
  ['brand', 'Brand'],
  ['ean', 'EAN'],
  ['extra_row', 'Optionale Spalte'],
  ['title_row', 'Lieferanten-Titel'],
  ['sellprice', 'Sellprice'],
  ['confirmed_sales', 'Conf. Sales'],
  ['estimated_sales', 'Est. Sales'],
  ['salesdrops', 'Salesdrops'],
  ['offers', 'Offers'],
  ['variations', 'Variations'],
  ['seasonality', 'Seasonality'],
  ['velocity_price', 'Velocity Price'],
  ['ai_analysis', 'AI Analysis'],
  ['ai_insight', 'AI Insight'],
  ['buyprice', 'Buyprice'],
]

let Center = ({ children }) => <div className='flex items-center justify-center'>{children}</div>

let arraysEqual = (a, b) =>
  a.length === b.length && a.every((value, index) => value === b[index])

const parseFilenameFromHeader = (header) => {
  if (typeof header !== 'string') {
    return null
  }

  const filenameStarMatch = header.match(/filename\*=([^;]+)/i)
  if (filenameStarMatch && filenameStarMatch[1]) {
    const rawValue = filenameStarMatch[1].split("''").pop() || filenameStarMatch[1]
    try {
      return decodeURIComponent(rawValue.replace(/['"]/g, '').trim())
    } catch (error) {
      return rawValue.replace(/['"]/g, '').trim()
    }
  }

  const filenameMatch = header.match(/filename="?([^\";]+)"?/i)
  if (filenameMatch && filenameMatch[1]) {
    return filenameMatch[1].trim()
  }

  return null
}

// Lazy Chart Tooltip Button - lädt Chart nur beim Hover
function LazyChartTooltipButton({ chartSrc }) {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)

  useEffect(() => {
    if (isTooltipVisible) {
      setImageLoaded(false)
    }
  }, [isTooltipVisible])

  if (!chartSrc) {
    return (
      <Button variant='primary' className='min-w-max' disabled>
        <ChartIcon className='h-4 w-max' />
      </Button>
    )
  }

  const tooltipContent = isTooltipVisible ? (
    <div>
      {!imageLoaded && (
        <div className='p-4 flex items-center justify-center'>
          <div className='w-4 h-4 border-2 border-gray-400 dark:border-gray-300 border-t-transparent rounded-full animate-spin'></div>
        </div>
      )}
      <img 
        src={chartSrc} 
        referrerPolicy="no-referrer"
        onLoad={() => setImageLoaded(true)}
        style={{ display: imageLoaded ? 'block' : 'none' }}
        alt="Keepa Chart"
      />
    </div>
  ) : (
    <div className='p-4 flex items-center justify-center'>
      <KeepaIcon className='w-8 h-8 text-neutral-400 dark:text-neutral-500' />
    </div>
  )

  return (
    <Tippy
      placement='bottom'
      onShow={() => setIsTooltipVisible(true)}
      onHide={() => setIsTooltipVisible(false)}
      content={tooltipContent}
    >
      <span>
        <Button variant='primary' className='min-w-max'>
          <ChartIcon className='h-4 w-max' />
        </Button>
      </span>
    </Tippy>
  )
}

export default function ProductTable({ listId, favoriteId, supplierPrefix }) {
  let [selectedProduct, setSelectedProduct] = useState(null)
  let [selectedRowId, setSelectedRowId] = useState(null)
  const queryClient = useQueryClient()
  const membership = useSelector((state) => {
    const homeMembership = state.auth.homeData?.membership
    if (homeMembership) return homeMembership
    return state.auth.authData?.membership
  })
  const normalizedMembership = typeof membership === 'string'
    ? membership.trim().toUpperCase()
    : ''
  const canUseMultiRegion = MULTI_REGION_MEMBERSHIPS.has(normalizedMembership)

  let [viewMode, setViewMode] = useLocalStorageState(`${application}-view-mode`, {
    defaultValue: 'table'
  })

  let [gridHiddenColumnIds, setGridHiddenColumnIds] = useLocalStorageState(`${GRID_COLUMN_CHOOSER_APP}-col-hidden`, {
    defaultValue: []
  })
  // Gleiche Keys wie die Hooks in ProductGrid (use-local-storage-state synct):
  // nur fuer den Multiview-Reset im Ansicht/Spalten-Popover noetig.
  let [gridColumnOrder, setGridColumnOrder] = useLocalStorageState(`${GRID_STORAGE_KEY_PREFIX}-col-order`)
  let [gridColumnSizing, setGridColumnSizing] = useLocalStorageState(`${GRID_STORAGE_KEY_PREFIX}-col-sizing`, {
    defaultValue: {}
  })

  const [tableRegion, setTableRegion] = useState(null)

  useEffect(() => {
    if (viewMode === 'grid') {
      setViewMode('multiview')
    }
  }, [setViewMode, viewMode])

  // States und Mutation vor ALL_COLUMNS, damit Column-Definitionen Zugriff haben.
  let [sort, setSort] = useState({ sortBy: undefined, sortType: undefined })
  const [storedPageSize, setStoredPageSize] = useLocalStorageState(`${application}-page-size`, {
    defaultValue: 20
  })
  const [pageIndex, setPageIndex] = useState(0)
  const pagination = useMemo(() => ({
    pageIndex,
    pageSize: ALLOWED_PAGE_SIZES.includes(storedPageSize) ? storedPageSize : 20,
  }), [pageIndex, storedPageSize])
  const setPagination = useCallback((updater) => {
    const next = typeof updater === 'function' ? updater(pagination) : updater
    const safeSize = ALLOWED_PAGE_SIZES.includes(next.pageSize) ? next.pageSize : 20
    const safePage = Number.isInteger(next.pageIndex) && next.pageIndex >= 0 ? next.pageIndex : 0
    if (safeSize !== storedPageSize) {
      setStoredPageSize(safeSize)
    }
    setPageIndex(safePage)
  }, [pagination, storedPageSize, setStoredPageSize])
  let [search, setSearch] = useState('')
  let onSearchDebounced = useDebouncedCallback((value) => setSearch(value), 500)
  // Kontrollierter Wert nur fuer die Such-Pille (Clear-Button);
  // das eigentliche Suchen laeuft weiter debounced ueber setSearch.
  let [searchText, setSearchText] = useState('')
  const handleSearch = (value) => {
    setSearchText(value)
    onSearchDebounced(value)
  }

  // Solange nach einem Kontext-Reset (Filter/Suche/Sort/Region) noch keine frische
  // Antwort da ist, zeigt placeholderData die alten Daten -> alte Gesamtseitenzahl im
  // Footer. Flag klemmt die Totals bis zum Eintreffen der Antwort auf die Reset-Seite.
  const [totalsResetPending, setTotalsResetPending] = useState(false)

  // Laufende Filter-Writes: Daten-Query pausieren, bis der letzte Write serverseitig
  // bestaetigt ist (sonst Fetch mit altem Filterstand); Lade-Anzeige schon ab dem
  // ersten Klick bei 'data'-Writes. Details: services/filters.js (filterWriteKey).
  const pendingFilterWrites = useIsMutating({ mutationKey: ['filter-write', application] })
  const pendingDataFilterWrites = useIsMutating({ mutationKey: ['filter-write', application, 'data'] })

  // Offenes Filter-Panel = Hold: Daten-Query pausiert, pageIndex-Reset aufgeschoben
  // bis zum Schliessen -- Panel-Edits sollen die Tabelle nicht live umladen.
  const filterPanelOpen = useFilterStore((s) => Boolean(s.openFilterPanels?.[application]))
  const [pendingFilterPageReset, setPendingFilterPageReset] = useState(false)

  // Filteraenderungen (Filters-Komponente/Presets) invalidieren nur die Queries und
  // erreichen diese Komponente sonst nicht -- Filter-Cache beobachten und bei echter
  // Aenderung auf Seite 1 zurueckspringen, sonst zeigt der alte pageIndex ins neue
  // (kleinere) Ergebnis und die Seite ist leer.
  const { data: filtersData } = useFiltersQuery(application)
  const filtersSignature = filtersData === undefined
    ? undefined
    : JSON.stringify(filtersData?.current_filter ?? null)
  const lastFiltersSignatureRef = useRef()
  useEffect(() => {
    if (filtersSignature === undefined) return
    const prev = lastFiltersSignatureRef.current
    lastFiltersSignatureRef.current = filtersSignature
    if (prev !== undefined && prev !== filtersSignature) {
      if (filterPanelOpen) {
        setPendingFilterPageReset(true)
        return
      }
      setPageIndex(0)
      setTotalsResetPending(true)
    }
  }, [filtersSignature, filterPanelOpen])

  // Panel zu -> aufgeschobenen Reset anwenden (Query bleibt bis dahin disabled,
  // damit nicht erst der alte Key und dann der Reset-Key fetcht).
  useEffect(() => {
    if (!filterPanelOpen && pendingFilterPageReset) {
      setPendingFilterPageReset(false)
      setPageIndex(0)
      setTotalsResetPending(true)
    }
  }, [filterPanelOpen, pendingFilterPageReset])

  // Suche/Sortierung/Region/Listen-Wechsel -> ebenfalls zurueck auf Seite 1.
  useEffect(() => {
    setPageIndex(0)
    setTotalsResetPending(true)
  }, [listId, favoriteId, supplierPrefix, search, sort.sortBy, sort.sortType, tableRegion])

  // B2B: tableRegion startet null und wird per Effect auf 'de' gesetzt - ohne
  // Fallback im Key feuert die Query 2x (Key [..., null], dann [..., 'de']).
  // Im API-Call wird sowieso `tableRegion || 'de'` benutzt -> gleicher Fallback hier.
  const tableQueryKey = ['data', application, listId, favoriteId, supplierPrefix, search, sort.sortBy, sort.sortType, pagination.pageIndex, pagination.pageSize, supplierPrefix ? (tableRegion || 'de') : tableRegion]
  const [pendingRowIds, setPendingRowIds] = useState(() => new Set())
  const updateWholesaleEntryMutation = useMutation({
    mutationFn: updateWholesaleEntryAPI,
    onMutate: ({ publicId }) => {
      setPendingRowIds((prev) => {
        const next = new Set(prev)
        next.add(publicId)
        return next
      })
    },
    onSuccess: (response, variables) => {
      if (!response?.success) return
      const updated = response.product
      const publicId = variables.publicId
      queryClient.setQueryData(tableQueryKey, (old) => {
        if (!old?.products) return old
        return {
          ...old,
          products: old.products.map((p) => p.public_id === publicId ? updated : p),
        }
      })
    },
    onSettled: (_data, _error, variables) => {
      setPendingRowIds((prev) => {
        const next = new Set(prev)
        next.delete(variables.publicId)
        return next
      })
    },
  })

  const ALL_COLUMNS = useMemo(() => {
    const renderSupplierLinkedPill = (value, row) => {
      const displayValue = value ?? '-'
      const supplierHref = buildSupplierProductUrl(supplierPrefix, row)
      if (!supplierHref) {
        return <Center><Pill>{displayValue}</Pill></Center>
      }

      return (
        <Center>
          <a href={supplierHref} target='_blank' rel='noopener noreferrer' className='no-underline'>
            <Pill className='cursor-pointer text-primary'>{displayValue}</Pill>
          </a>
        </Center>
      )
    }

    let columns = [
      { header: 'ASIN', accessorKey: 'asin', cell: ({ getValue }) => <Center><Pill>{getValue()}</Pill></Center> },
      { header: 'PRODUCT ID', accessorKey: 'product_id_list', cell: ({ getValue, row }) => renderSupplierLinkedPill(getValue(), row.original) },
      { header: 'EAN / ID', accessorKey: 'ean', cell: ({ getValue, row }) => renderSupplierLinkedPill(getValue(), row.original) },
      {
        header: 'OPTIONALE SPALTE',
        accessorKey: 'extra_row',
        cell: ({ getValue }) => <Center><Pill>{getValue() ?? '-'}</Pill></Center>
      },
      {
        header: 'LIEFERANTEN-TITEL',
        accessorKey: 'title_row',
        cell: ({ getValue }) => <Center><Pill>{getValue() ?? '-'}</Pill></Center>
      },
      { header: 'TITLE', accessorKey: 'name', cell: ({ getValue }) => <Center><Pill>{getValue()}</Pill></Center> },
    {
      header: 'MARGIN',
      accessorKey: 'margin',
      cell(info) {
        let value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return (
          <div className='text-center'>
            <Pill
              color={
                value < 4 ? 'red' :
                  value < 7 ? 'orange' :
                    'green'
              }
            >{value}%</Pill>
          </div>
        )
      }
    },
    {
      header: 'EINHEITEN',
      accessorKey: 'packageQuantity',
      cell(info) {
        const row = info.row.original
        const publicId = row?.public_id
        const value = info.getValue() ?? 1
        const userValue = row?.packageQuantityUser ?? null
        const canEdit = Boolean(!favoriteId && !supplierPrefix && publicId)
        const isPending = canEdit && pendingRowIds.has(publicId)
        return (
          <EditablePackageQuantityCell
            value={value}
            userValue={userValue}
            editable={canEdit}
            isPending={isPending}
            onSubmit={(newValue) => {
              updateWholesaleEntryMutation.mutate({
                listId,
                publicId,
                field: 'package_quantity',
                value: newValue,
                regions: tableRegion ? [tableRegion] : undefined,
              })
            }}
          />
        )
      }
    },
    {
      header: 'ROI',
      accessorKey: 'roi',
      cell(info) {
        const value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return (
          <div className='text-center'>
            <Pill
              color={
                value < 4 ? 'red' :
                  value < 7 ? 'orange' :
                    'green'
              }
            >{value}%</Pill>
          </div>
        )
      }
    },
    {
      header: 'PROFIT',
      accessorKey: 'profit',
      cell(info) {
        const value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return (
          <div className='text-center'>
            <Pill
              color={
                value < 4 ? 'red' :
                  value < 7 ? 'orange' :
                    'green'
              }
            >{currencyFormatter(value)}€</Pill>
          </div>
        )
      }
    },
    {
      header: 'MARGIN 30D',
      accessorKey: 'margin30',
      cell(info) {
        const value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return (
          <div className='text-center'>
            <Pill
              color={
                value < 4 ? 'red' :
                  value < 7 ? 'orange' :
                    'green'
              }
            >{value}%</Pill>
          </div>
        )
      }
    },
    {
      header: 'ROI 30D',
      accessorKey: 'roi30',
      cell(info) {
        const value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return (
          <div className='text-center'>
            <Pill
              color={
                value < 4 ? 'red' :
                  value < 7 ? 'orange' :
                    'green'
              }
            >{value}%</Pill>
          </div>
        )
      }
    },
    {
      header: 'PROFIT 30D',
      accessorKey: 'profit30',
      cell(info) {
        const value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return (
          <div className='text-center'>
            <Pill
              color={
                value < 4 ? 'red' :
                  value < 7 ? 'orange' :
                    'green'
              }
            >{currencyFormatter(value)}€</Pill>
          </div>
        )
      }
    },
    {
      header: 'CATEGORY',
      accessorKey: 'category',
      cell: ({ getValue }) => <Center><Pill>{getValue()}</Pill></Center>
    },
    {
      header: 'BSR',
      accessorKey: 'salesrank',
      cell(info) {
        let value = info.getValue();
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }
        return <Center><Pill>{value}</Pill></Center>
      }
    },
    {
      header: 'BSR %',
      accessorKey: 'percent_bsr',
      cell(info) {
        let value = info.getValue();
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }
        return <Center><Pill>{value}%</Pill></Center>
      }
    },
    {
      header: 'COST',
      accessorKey: 'buyprice',
      cell: (info) => {
        const row = info.row.original
        const publicId = row?.public_id
        const value = info.getValue()
        const canEdit = Boolean(!favoriteId && !supplierPrefix && publicId)
        const isPending = canEdit && pendingRowIds.has(publicId)
        return (
          <EditableBuypriceCell
            value={value}
            editable={canEdit}
            isPending={isPending}
            onSubmit={(newValue) => {
              updateWholesaleEntryMutation.mutate({
                listId,
                publicId,
                field: 'price',
                value: newValue,
                regions: tableRegion ? [tableRegion] : undefined,
              })
            }}
          />
        )
      }
    },
    {
      header: 'SELL PRICE',
      accessorKey: 'sellprice',
      cell: (info) => {
        const value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return <Center><Pill>€{currencyFormatter(Number(value))}</Pill></Center>
      }

    },
    {
      header: 'SELL PRICE 30D',
      accessorKey: 'sellprice30',
      cell: (info) => {
        const value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return <Center><Pill>€{currencyFormatter(Number(value))}</Pill></Center>
      }
    },
    {
      header: 'INFO',
      enableResizing: false,
      cell: ({ row }) => {
        return (
          <div className='flex items-center justify-center gap-2 min-w-max'>
            <LazyChartTooltipButton chartSrc={row.original.keepa_chart_dk} />

            <Tippy
              placement='bottom'
              content={<img src={row.original.amz_img} />}
            >
              <span>
                <Button variant='primary' className='min-w-max'>
                  <ImgIcon className='h-4 w-max' />
                </Button>
              </span>
            </Tippy>

            <Button variant='primary' className='min-w-max' href={row.original.amz_url} target='_blank'>
              <ExportIcon className='h-4 w-max' />
            </Button>

            <Tippy placement='bottom' content={<div className='text-xs'>{row.original.mismatch_warning ? 'Achtung: erhöhte Mismatch-Gefahr' : 'Kein Mismatch erkannt'}</div>}>
              <span>
                <Button variant='primary' className={classNames('min-w-max', row.original.mismatch_warning ? 'text-amber-500' : 'opacity-30')}>
                  <MismatchIcon />
                </Button>
              </span>
            </Tippy>
          </div>
        );
      }
    },
    {
      header: 'TOOLS',
      enableResizing: false,
      cell: ({ row }) => {
        const supplierHref = buildSupplierProductUrl(supplierPrefix, row.original)
        return (
          <div className='flex items-center justify-center gap-2 min-w-max'>
            {supplierHref && (
              <Tippy placement='bottom' content='Eany öffnen'>
                <span>
                  <Button variant='primary' className='min-w-max' href={supplierHref} target='_blank' rel='noopener noreferrer'>
                    <ExportIcon className='h-4 w-max' />
                  </Button>
                </span>
              </Tippy>
            )}
            <Button variant='primary' className='min-w-max' href={row.original.a1_url} target='_blank'>
              <img src={a1Img} className='w-max h-4 dark:invert' />
            </Button>
            <Button variant='primary' className='min-w-max' href={row.original.keepa_url} target='_blank'>
              <KeepaIcon className='w-max h-4' />
            </Button>
          </div>
        );
      }
    },
    {
      header: 'AI ANALYSIS',
      accessorKey: 'ai_analysis',
      enableResizing: false,
      enableSorting: false,
      cell: ({ getValue }) => {
        const value = getValue()
        return (
          <Center>
            <AIAnalysisTag analysis={value} />
          </Center>
        )
      }
    },
    {
      header: 'AI INSIGHT',
      id: 'ai_insight',
      enableResizing: false,
      cell: ({ row }) => {
        const aiRegion = (typeof tableRegion === 'string' && tableRegion) || row.original?.primaryRegion
        const regionCost = aiRegion
          ? row.original?.regions?.[aiRegion]?.cost ??
            row.original?.regions?.[aiRegion]?.buyprice ??
            row.original?.cost ??
            row.original?.buyprice
          : row.original?.cost ?? row.original?.buyprice

        return (
          <AICheckButton
            asin={row.original?.asin}
            region={aiRegion}
            cost={regionCost}
          />
        )
      }
    },
    {
      header: 'FAVORITE',
      enableResizing: false,
      cell: ({ row }) => {
        return (
          <div className='flex items-center justify-center gap-2 min-w-max'>
            <Button
              className='min-w-max'
              onClick={() => {
                setSelectedProduct(row.original);
              }}
            >
              <FavoriteStar active={!!row.original.favorite} />
            </Button>
          </div>
        );
      }
    },
    {
      header: 'CONF. SALES',
      accessorKey: 'confirmed_sales',
      cell(info) {
        let value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return (
          <div className='text-center'>
            <Pill
              color={
                value < 10 ? 'red' :
                  value < 50 ? 'orange' :
                    'green'
              }
            >{value}</Pill>
          </div>
        )
      }
    },
    {
      header: 'EST. SALES',
      accessorKey: 'estimated_sales',
      cell(info) {
        let value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }

        return (
          <div className='text-center'>
            <Pill
              color={
                value < 5 ? 'red' :
                  value < 30 ? 'orange' :
                    'green'
              }
            >{value < 5 ? '< 5' : value}</Pill>
          </div>
        )
      }
    },
    {
      header: 'SALESDROPS',
      accessorKey: 'salesdrops',
      cell(info) {
        let value = info.getValue()
        if (value === null) { return "-"; }
        return <div className='text-center'>
          <Pill
            color={
              value < 5 ? 'red' :
                value < 30 ? 'orange' :
                  'green'
            }
          >{value}</Pill>
        </div>
      }
    },
    {
      header: 'OFFERS',
      accessorKey: 'offers',
      cell(info) {
        let value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }
        return <Center><Pill>{value}</Pill></Center>
      }
    },
    {
      header: 'VARIATIONS',
      accessorKey: 'variations',
      cell(info) {
        let value = info.getValue()
        if (value === null || value === undefined) {
          return <Center><Pill>-</Pill></Center>
        }
        return <Center><Pill>{value}</Pill></Center>
      }
    },
    {
      header: 'SEASONALITY',
      accessorKey: 'seasonality',
      cell: ({ getValue }) => <SeasonalityCell value={getValue()} />
    },
    {
      header: 'VELOCITY PRICE',
      accessorKey: 'velocity_price',
      cell: ({ getValue }) => <VelocityPriceCell value={getValue()} />
    },
    {
      header: 'BRAND',
      accessorKey: 'brand',
      cell(info) {
        let value = info.getValue();
        if (value === null) { return <span>-</span>; }
        return <Center><Pill>{value}</Pill></Center>;
      }
    },
  ]

    return columns.map(column => {
      if (column.id) {
        return column
      }

      if (column.accessorKey) {
        return { ...column, id: column.accessorKey }
      }

      if (typeof column.header === 'string') {
        return { ...column, id: column.header.toLowerCase().replace(/\s+/g, '_') }
      }

      return column
    })
  }, [tableRegion, favoriteId, listId, supplierPrefix, pendingRowIds, updateWholesaleEntryMutation])

  let handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['data', application] })
  }, [queryClient])

  let { data, isFetching, isError, error } = useQuery({
    queryKey: tableQueryKey,
    queryFn: () => supplierPrefix
      ? getB2BSupplierProductsAPI(
        supplierPrefix,
        tableRegion || 'de',
        search,
        sort.sortBy,
        sort.sortType,
        pagination.pageIndex,
        pagination.pageSize
      )
      : getWholesaleProductsAPI(
        listId,
        favoriteId,
        search,
        sort.sortBy,
        sort.sortType,
        pagination.pageIndex,
        pagination.pageSize,
        tableRegion ? [tableRegion] : undefined
      ),
    placeholderData: (prev) => prev,
    staleTime: 60000,
    // Pausiert, solange Filter-Writes laufen -- siehe pendingFilterWrites oben.
    enabled: pendingFilterWrites === 0 && !filterPanelOpen && !pendingFilterPageReset,
  })
  const isFileLoaded = Boolean(data && !isFetching)

  // Bei offenem Filter-Panel bleibt die Anzeige LIVE (ehrlich): ein beim Oeffnen
  // bereits laufender Fetch wird nicht gecancelt -- wird er fertig, erscheinen
  // die Daten auch hinterm Panel. Neue Requests entstehen waehrend der
  // Panel-Session keine (Edits silent, Query pausiert, Reload beim Schliessen).

  // B2B: kein COUNT(*) im Backend - Response liefert has_next (LIMIT+1-Probe).
  // pageCount = aktuelle Seite + 1 falls noch was kommt, sonst aktuelle Seite.
  // Pagination zeigt damit "Seite X von X+1" solange es weitergeht; Next wird
  // disabled, sobald has_next=false.
  const effectiveTotalPages = supplierPrefix
    ? (data?.has_next ? pagination.pageIndex + 2 : pagination.pageIndex + 1)
    : (data?.total_pages || 0)

  // Frische Antwort da -> Totals-Klemme wieder loesen. Solange noch Filter-Writes
  // laufen (Query pausiert, isFetching false), Klemme halten.
  useEffect(() => {
    if (totalsResetPending && !isFetching && pendingFilterWrites === 0) {
      setTotalsResetPending(false)
    }
  }, [totalsResetPending, isFetching, pendingFilterWrites])

  // Anzeige-Total: NUR die wirklich vom Server bekannte Zahl. Im B2B-Modus
  // (supplierPrefix: Server kennt nur has_next, keinen festen Total) und
  // waehrend einer Reset-Phase ist die Zahl unbekannt -> null, dann steht im
  // Footer einfach nichts neben dem Chip.
  const displayTotalPages = (totalsResetPending || supplierPrefix)
    ? null
    : (data?.total_pages || null)
  // Next-Button im B2B-Modus aus has_next steuern (sonst Fallback in der Bar).
  const displayHasNext = (supplierPrefix && data && !totalsResetPending)
    ? Boolean(data.has_next)
    : undefined

  // Prefetch der naechsten Seite im Hintergrund, sobald die aktuelle steht.
  // Key + queryFn spiegeln die Haupt-Query (nur pageIndex+1) -> der spaetere
  // Next-Klick ist ein Cache-Hit und damit instant.
  useEffect(() => {
    // Bei offenem Filter-Panel keinen Prefetch starten (UI-Freeze, Filter
    // koennen sich gerade aendern -- die Seite wuerde umsonst geladen).
    if (filterPanelOpen) return
    if (!data) return
    const hasMore = supplierPrefix
      ? Boolean(data.has_next)
      : (pagination.pageIndex + 1 < (data.total_pages || 0))
    if (!hasMore) return

    const nextPage = pagination.pageIndex + 1
    const nextKey = ['data', application, listId, favoriteId, supplierPrefix, search, sort.sortBy, sort.sortType, nextPage, pagination.pageSize, supplierPrefix ? (tableRegion || 'de') : tableRegion]
    queryClient.prefetchQuery({
      queryKey: nextKey,
      queryFn: () => supplierPrefix
        ? getB2BSupplierProductsAPI(supplierPrefix, tableRegion || 'de', search, sort.sortBy, sort.sortType, nextPage, pagination.pageSize)
        : getWholesaleProductsAPI(listId, favoriteId, search, sort.sortBy, sort.sortType, nextPage, pagination.pageSize, tableRegion ? [tableRegion] : undefined),
      staleTime: 60000,
    })
  }, [data, supplierPrefix, application, listId, favoriteId, search, sort.sortBy, sort.sortType, pagination.pageIndex, pagination.pageSize, tableRegion, queryClient, filterPanelOpen])

  // Beim Seitenwechsel/PageSize-Wechsel zurueck nach oben. Bei vorgeladener Seite
  // tauscht react-table die Rows synchron NACH dem scroll -> Browser-Scroll-
  // Anchoring zieht die Position zurueck. Deshalb scrollTop=0 sofort + nochmal im
  // naechsten Frame; overflowAnchor:'none' am Container (unten) verhindert das
  // Zurueckziehen ganz.
  const tableScrollRef = useRef(null)
  const scrollPageToTop = useCallback(() => {
    scrollToTopFrom(tableScrollRef.current)
  }, [])

  useEffect(() => {
    scrollPageToTop()
    const id = requestAnimationFrame(scrollPageToTop)
    return () => cancelAnimationFrame(id)
  }, [pagination.pageIndex, pagination.pageSize, scrollPageToTop])

  const { data: wholesaleFiles } = useWholesaleAllFilesQuery()
  const { mutateAsync: exportWholesaleList, isPending: isExportingList } = useWholesaleExportListMutation()

  const [storedMarketplaces, setStoredMarketplaces] = useLocalStorageState('wholesale-marketplaces', {
    defaultValue: {}
  })

  const storageKey = useMemo(() => {
    if (supplierPrefix) {
      return `supplier-${supplierPrefix}`
    }
    if (favoriteId) {
      return `favorite-${favoriteId}`
    }
    if (listId) {
      return `list-${listId}`
    }
    return null
  }, [favoriteId, listId, supplierPrefix])

  const currentFile = useMemo(() => {
    if (!Array.isArray(wholesaleFiles)) {
      return null
    }

    if (favoriteId) {
      return wholesaleFiles.find(file => String(file.favorite_id) === String(favoriteId)) ?? null
    }

    if (listId) {
      return wholesaleFiles.find(file => String(file.list_id) === String(listId)) ?? null
    }

    return null
  }, [favoriteId, listId, wholesaleFiles])

  const canExportList = !supplierPrefix && Boolean(
    (currentFile && (currentFile.list_id || currentFile.favorite_id)) ?? listId ?? favoriteId
  )

  const resolvedMarketplaces = useMemo(() => {
    const marketplacesFromData = Array.isArray(data?.marketplaces) ? data.marketplaces : []
    const marketplacesFromFile = Array.isArray(currentFile?.marketplaces) ? currentFile.marketplaces : []

    const normalized = [...marketplacesFromData, ...marketplacesFromFile]
      .map(code => String(code || '').toLowerCase())
      .filter(Boolean)

    return normalized.length ? Array.from(new Set(normalized)).sort() : []
  }, [currentFile?.marketplaces, data?.marketplaces])

  const storedMarketplacesForKey = useMemo(() => {
    if (!storageKey) {
      return []
    }
    const stored = storedMarketplaces?.[storageKey]
    if (!Array.isArray(stored)) {
      return []
    }
    return stored.map(code => String(code || '').toLowerCase()).filter(Boolean)
  }, [storageKey, storedMarketplaces])

  useEffect(() => {
    if (!storageKey || !resolvedMarketplaces.length) {
      return
    }

    setStoredMarketplaces((prev = {}) => {
      const previousList = Array.isArray(prev[storageKey]) ? prev[storageKey] : []
      if (arraysEqual(previousList, resolvedMarketplaces)) {
        return prev
      }
      return {
        ...prev,
        [storageKey]: resolvedMarketplaces,
      }
    })
  }, [resolvedMarketplaces, setStoredMarketplaces, storageKey])

  const availableGridRegions = useMemo(() => {
    if (favoriteId) {
      return SUPPORTED_GRID_REGIONS
    }

    const candidates = resolvedMarketplaces.length ? resolvedMarketplaces : storedMarketplacesForKey

    if (candidates.length) {
      return candidates
    }

    const regionCandidates = [
      typeof data?.region === 'string' ? data.region : null,
      typeof currentFile?.region === 'string' ? currentFile.region : null,
      typeof currentFile?.marketplace === 'string' ? currentFile.marketplace : null,
    ].filter(Boolean)

    if (regionCandidates.length) {
      return [String(regionCandidates[0]).toLowerCase()]
    }

    return SUPPORTED_GRID_REGIONS
  }, [currentFile?.marketplace, currentFile?.region, data?.region, favoriteId, resolvedMarketplaces, storedMarketplacesForKey])

  const defaultGridRegions = useMemo(() => {
    if (availableGridRegions.length) {
      const normalizedRegion = typeof data?.region === 'string' ? data.region.toLowerCase() : undefined
      if (normalizedRegion && availableGridRegions.includes(normalizedRegion)) {
        return [normalizedRegion]
      }
      return [availableGridRegions[0]]
    }
    return [SUPPORTED_GRID_REGIONS[0]]
  }, [availableGridRegions, data?.region])

  useEffect(() => {
    if (!availableGridRegions.length) {
      if (tableRegion !== null) {
        setTableRegion(null)
      }
      return
    }

    if (!tableRegion || !availableGridRegions.includes(tableRegion)) {
      setTableRegion(availableGridRegions[0])
    }
  }, [availableGridRegions, tableRegion])

  const [gridRegions, setGridRegions] = useState(defaultGridRegions)

  useEffect(() => {
    if (viewMode === 'multiview') {
      if (!(gridRegions?.length) && defaultGridRegions?.length) {
        setGridRegions(defaultGridRegions)
      }
    } else if (!arraysEqual(gridRegions ?? [], defaultGridRegions)) {
      setGridRegions(defaultGridRegions)
    }
  }, [defaultGridRegions, gridRegions, viewMode])

  useEffect(() => {
    const currentRegions = gridRegions ?? []
    const filteredRegions = currentRegions.filter((region) => availableGridRegions.includes(region))

    if (!filteredRegions.length) {
      if (defaultGridRegions.length && !arraysEqual(currentRegions, defaultGridRegions)) {
        setGridRegions(defaultGridRegions)
      }
      return
    }

    if (!arraysEqual(filteredRegions, currentRegions)) {
      setGridRegions(filteredRegions)
    }
  }, [availableGridRegions, defaultGridRegions, gridRegions])

  useEffect(() => {
    if (canUseMultiRegion) {
      return
    }
    setGridRegions((current = []) => {
      const normalized = Array.isArray(current) ? current.filter(Boolean) : []

      if (!normalized.length) {
        if (!defaultGridRegions.length) {
          return normalized
        }
        return [defaultGridRegions[0]]
      }

      if (normalized.length === 1) {
        return normalized
      }

      return [normalized[0]]
    })
  }, [canUseMultiRegion, defaultGridRegions])

  const activeGridRegions = useMemo(() => {
    const normalized = Array.isArray(gridRegions) ? gridRegions.filter(Boolean) : []
    if (canUseMultiRegion) {
      return normalized
    }
    if (normalized.length) {
      return [normalized[0]]
    }
    if (defaultGridRegions.length) {
      return [defaultGridRegions[0]]
    }
    return []
  }, [canUseMultiRegion, gridRegions, defaultGridRegions])

  const headerRegion = useMemo(() => {
    if (viewMode === 'table') {
      return typeof tableRegion === 'string' ? tableRegion.toUpperCase() : null
    }
    const region = activeGridRegions?.[0] ?? data?.region
    return typeof region === 'string' ? region.toUpperCase() : null
  }, [activeGridRegions, data?.region, tableRegion, viewMode])

  const tableProducts = useMemo(() => {
    if (!data?.products) {
      return []
    }

    const fallbackRegion = typeof data?.region === 'string' ? data.region.toLowerCase() : undefined
    const normalizedRegion = typeof tableRegion === 'string' && tableRegion ? tableRegion : fallbackRegion

    return data.products.map(product => {
      const supplierUrl = buildSupplierProductUrl(supplierPrefix, product)
      const regions = product?.regions || {}
      const regionKeys = Object.keys(regions)
      const primaryRegion =
        regionKeys.find(regionKey => regionKey === normalizedRegion) ??
        regionKeys[0] ??
        normalizedRegion

      const regionData = primaryRegion ? regions[primaryRegion] : undefined
      const keepaCharts = product.keepa_cahrts || product.keepa_charts
      const keepaChartDark =
        (primaryRegion && keepaCharts?.[primaryRegion]?.dark?.['90']) ??
        (primaryRegion && keepaCharts?.[primaryRegion]?.dark?.['180']) ??
        (primaryRegion && keepaCharts?.[primaryRegion]?.dark?.['30']) ??
        (primaryRegion && keepaCharts?.[primaryRegion]?.white?.['90']) ??
        product.keepa_chart_dk ??
        null

      const seasonalityDatasets = [
        {
          key: 'sales',
          title: 'Seasonality Sales',
          baseLabel: 'Conf. Sales',
          value: regionData?.seasonality_sales ?? regionData?.seasonality?.sales ?? product.seasonality_sales ?? product.seasonality?.sales ?? null
        },
        {
          key: 'bsr',
          title: 'Seasonality BSR',
          baseLabel: 'BSR',
          value: regionData?.seasonality_bsr ?? regionData?.seasonality?.bsr ?? product.seasonality_bsr ?? product.seasonality?.bsr ?? null
        },
        {
          key: 'bsrdrops',
          title: 'Seasonality BSR Drops',
          baseLabel: 'Salesdrops',
          value: regionData?.seasonality_bsrdrops ?? regionData?.seasonality?.bsrdrops ?? regionData?.seasonality?.bsr_drops ?? product.seasonality_bsrdrops ?? product.seasonality?.bsrdrops ?? product.seasonality?.bsr_drops ?? null
        },
      ]

      return {
        ...product,
        supplier_url: supplierUrl ?? product.supplier_url,
        primaryRegion,
        keepa_chart_dk: keepaChartDark,
        amz_url: regionData?.amz_url ?? product.amz_url ?? null,
        extra_row: product.extra_row ?? regionData?.extra_row ?? null,
        ai_analysis: resolveRegionAIAnalysis(regionData),
        margin: regionData?.margin ?? product.margin ?? null,
        packageQuantity: regionData?.packageQuantity ?? product.packageQuantity ?? 1,
        packageQuantityUser: regionData?.packageQuantityUser ?? product.packageQuantityUser ?? null,
        roi: regionData?.roi ?? product.roi ?? null,
        profit: regionData?.profit ?? product.profit ?? null,
        margin30: regionData?.margin30 ?? product.margin30 ?? null,
        roi30: regionData?.roi30 ?? product.roi30 ?? null,
        profit30: regionData?.profit30 ?? product.profit30 ?? null,
        sellprice: regionData?.sellprice ?? product.sellprice ?? null,
        cost: regionData?.cost ?? product.cost ?? product.buyprice ?? null,
        buyprice: regionData?.buyprice ?? product.buyprice ?? null,
        sellprice30: regionData?.sellprice30 ?? product.sellprice30 ?? null,
        confirmed_sales: regionData?.confirmed_sales ?? product.confirmed_sales ?? null,
        estimated_sales: regionData?.estimated_sales ?? product.estimated_sales ?? null,
        salesdrops: regionData?.salesdrops ?? product.salesdrops ?? null,
        offers: regionData?.offers ?? product.offers ?? null,
        variations: regionData?.variations ?? product.variations ?? null,
        salesrank: regionData?.salesrank ?? product.salesrank ?? null,
        percent_bsr: regionData?.percent_bsr ?? product.percent_bsr ?? null,
        seasonality: seasonalityDatasets,
        velocity_price: computeVelocityPriceRange(seasonalityDatasets),
      }
    })
  }, [data?.products, data?.region, supplierPrefix, tableRegion])

  const handleExportList = useCallback(async () => {
    const resolvedListId = (currentFile && currentFile.list_id) ?? listId
    const resolvedFavoriteId = (currentFile && currentFile.favorite_id) ?? favoriteId
    if (!resolvedListId && !resolvedFavoriteId) {
      toast.error('Keine Liste oder Favorit zum Exportieren gefunden.')
      return
    }

    const selectedRegions = viewMode === 'multiview'
      ? activeGridRegions
      : (tableRegion ? [tableRegion] : [])

    const normalizedRegions = (selectedRegions ?? [])
      .map(region => String(region || '').toLowerCase())
      .filter(Boolean)

    const fallbackRegions = normalizedRegions.length
      ? normalizedRegions
      : (availableGridRegions.length
        ? [availableGridRegions[0]]
        : (typeof data?.region === 'string' ? [data.region.toLowerCase()] : []))

    if (!fallbackRegions.length) {
      toast.error('Keine Märkte für den Export vorhanden.')
      return
    }

    try {
      const response = await exportWholesaleList({
        listId: resolvedListId,
        favoriteId: resolvedListId ? null : resolvedFavoriteId,
        regions: fallbackRegions,
      })

      const blobData = response?.data
      if (!(blobData instanceof Blob)) {
        toast.error('Unerwartetes Export-Format erhalten.')
        return
      }

      const headerFileName = parseFilenameFromHeader(response?.headers?.['content-disposition'])
      const dashboardFileName = typeof data?.filename === 'string'
        ? data.filename.trim()
        : (typeof currentFile?.filename === 'string' ? currentFile.filename.trim() : null)

      const fallbackBaseName = dashboardFileName && dashboardFileName.length
        ? dashboardFileName.replace(/\.[^.]+$/, '')
        : (resolvedListId ? `wholesale-list-${resolvedListId}` : `wholesale-favorite-${resolvedFavoriteId}`)

      const fallbackFileName = `${fallbackBaseName}.xlsx`

      const url = window.URL.createObjectURL(blobData)
      const link = document.createElement('a')
      link.href = url
      link.download = headerFileName || fallbackFileName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      toast.success('Export wird gestartet.')
    } catch (error) {
      console.error('Error exporting wholesale list', error)
    }
  }, [activeGridRegions, availableGridRegions, currentFile?.filename, data?.filename, exportWholesaleList, listId, tableRegion, viewMode])

  const handleExportButtonClick = useCallback((event) => {
    event?.preventDefault?.()
    if (isExportingList || !canExportList || !isFileLoaded) {
      return
    }
    handleExportList()
  }, [canExportList, handleExportList, isExportingList, isFileLoaded])

  // No inner scroll container; page section scrolls

  let [hiddenColumnIds, setHiddenColumnIds] = useLocalStorageState(`${TABLE_STORAGE_KEY_PREFIX}-col-hidden`, {
    defaultValue: []
  })
  let [columnOrder, setColumnOrder] = useLocalStorageState(`${TABLE_STORAGE_KEY_PREFIX}-col-order`, {
    defaultValue: []
  })
  let [columnSizing, setColumnSizing] = useLocalStorageState(`${TABLE_STORAGE_KEY_PREFIX}-col-sizing`, {
    defaultValue: {}
  })

  const headerToIdMap = useMemo(() => ALL_COLUMNS.reduce((acc, column) => {
    if (typeof column.header === 'string' && column.id) {
      acc[column.header] = column.id
    }
    return acc
  }, {}), [ALL_COLUMNS])

  const normalizedHiddenColumnIds = useMemo(
    () => hiddenColumnIds.map((id) => headerToIdMap[id] ?? id),
    [hiddenColumnIds, headerToIdMap]
  )

  useEffect(() => {
    if (!arraysEqual(hiddenColumnIds, normalizedHiddenColumnIds)) {
      let sanitized = Array.from(new Set(normalizedHiddenColumnIds.filter(Boolean)))
      setHiddenColumnIds(sanitized)
    }
  }, [hiddenColumnIds, normalizedHiddenColumnIds, setHiddenColumnIds])

  const columns = useMemo(() =>
    ALL_COLUMNS.filter(column => !normalizedHiddenColumnIds.includes(column.id)), [ALL_COLUMNS, normalizedHiddenColumnIds])

  useColumnSettingsLoader({
    module: TABLE_SETTINGS_MODULE,
    setColumnOrder,
    setHiddenColumnIds,
    setColumnSizing,
  })

  let table = useReactTable({
    columns,
    columnResizeMode: 'onChange',
    columnResizeDirection: 'ltr',
    data: tableProducts,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    onPaginationChange: setPagination,
    pageCount: effectiveTotalPages,
    state: {
      pagination,
      columnOrder,
      columnSizing,
    },
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
  })

  useEffect(() => {
    if (!Array.isArray(columnOrder)) {
      return
    }
    // Gegen ALLE Spalten abgleichen, nicht nur die sichtbaren: ausgeblendete
    // behalten ihre Position in der Order und tauchen beim Wiedereinblenden
    // an alter Stelle auf (statt ans Ende zu rutschen).
    const columnIds = ALL_COLUMNS.map((col) => col.id)
    const filtered = columnOrder.filter((id) => columnIds.includes(id))
    const missing = columnIds.filter((id) => !filtered.includes(id))
    const next = [...filtered, ...missing]
    if (!arraysEqual(next, columnOrder)) {
      setColumnOrder(next)
    }
  }, [columnOrder, ALL_COLUMNS, setColumnOrder])

  // Column drag & drop
  let handleDragStart = useCallback((e, columnId) => {
    e.dataTransfer.setData('text/plain', columnId)
    e.dataTransfer.effectAllowed = 'move'
  }, [])

  let handleDragOver = useCallback((e) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [])

  let handleDrop = useCallback((e, targetColumnId) => {
    e.preventDefault()
    let sourceColumnId = e.dataTransfer.getData('text/plain')

    if (sourceColumnId === targetColumnId) {
      return
    }

    let currentColumnOrder = table.getState().columnOrder
    if (!currentColumnOrder?.length) {
      currentColumnOrder = table.getAllLeafColumns().map(column => column.id)
    }

    let sourceIndex = currentColumnOrder.indexOf(sourceColumnId)
    let targetIndex = currentColumnOrder.indexOf(targetColumnId)

    // Pin-Guard gegen die Laenge der ORDER pruefen (enthaelt auch versteckte
    // IDs) -- gegen die sichtbare Spaltenzahl wuerden Drops am Ende blocken.
    if (sourceIndex === -1 || targetIndex === -1 || targetIndex >= currentColumnOrder.length - RIGHT_PINNED_COLUMNS) {
      return
    }

    let newColumnOrder = [...currentColumnOrder]
    newColumnOrder.splice(sourceIndex, 1)
    newColumnOrder.splice(targetIndex, 0, sourceColumnId)

    setColumnOrder(newColumnOrder)
  }, [table])

  let handleDragEnter = useCallback((e, columnId) => {
    setDragOverColumnId(columnId);
  }, [])

  let handleDragLeave = useCallback(() => {
    setDragOverColumnId(null)
  }, [])

  let [dragOverColumnId, setDragOverColumnId] = useState(null)
  // \ Column drag & drop

  useColumnSettingsSync({
    module: TABLE_SETTINGS_MODULE,
    columnOrder,
    hiddenColumnIds,
    columnSizing,
  })

  if (isError) {
    return <div className="error">Error: {error.message}</div>;
  }

  const handlePageChange = (page) => {
    scrollPageToTop()
    setPagination({ ...pagination, pageIndex: page })
  }

  const handlePageSizeChange = (size) => {
    scrollPageToTop()
    setPagination({ pageIndex: 0, pageSize: size })
  }

  const subHeader = (
    <>
      <header className="px-4 pt-4 pb-5">
        {/* Titelzeile: Modulname + Datei/Markt links, Navigation (Zurück) rechts.
            Die Daten-Controls leben getrennt darunter in der Toolbar-Bar. */}
        <div className="flex items-start justify-between gap-2">
          <Subheader
            subtitle={
              <div className='flex items-center gap-2 items-center'>
                {headerRegion && !supplierPrefix &&
                  <Flag country={headerRegion} size={16} />
                }
                {!supplierPrefix && data?.filename}
              </div>
            }
          >
            Wholesale <em>Wizard</em>
          </Subheader>

          <a
            href='/wholesale'
            className='shrink-0 inline-flex items-center gap-1 h-9 px-3 rounded-lg border border-neutral-200 dark:border-neutral-700 text-sm font-medium text-textTitle bg-background hover:border-primary transition-colors'
          >
            <i className='bx bx-chevron-left text-base leading-none' aria-hidden></i>
            Zurück
          </a>
        </div>

        {/* Toolbar-Bar: Controls in eigenem umrandeten Balken. Suche fuellt
            links (gekappt), Buttons + Export-CTA rechts. */}
        <div className='mt-5 flex flex-wrap items-center gap-2.5 p-2.5 rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-bgColor dark:bg-white/[0.02]'>
          {!supplierPrefix && (
            // Such-Pille — fuellt den Platz links
            <div className="relative flex-1 min-w-[220px] max-w-[380px] group">
              <i className="bx bx-search absolute left-3 top-1/2 -translate-y-1/2 text-base text-neutral-400 pointer-events-none group-focus-within:text-primary"></i>
              <input
                type="text"
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Produkt, ASIN oder EAN suchen"
                className="w-full h-9 pl-9 pr-9 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-background text-sm text-textTitle placeholder:text-neutral-400 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-colors"
              />
              {searchText && (
                <button
                  type="button"
                  onClick={() => handleSearch('')}
                  aria-label="Leeren"
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex items-center justify-center h-6 w-6 rounded text-neutral-400 hover:text-textTitle"
                >
                  <i className="bx bx-x text-lg leading-none"></i>
                </button>
              )}
            </div>
          )}

          <span className='flex-1'></span>

          {viewMode === 'table' && availableGridRegions.length > 1 && (
            <RegionManager
              regions={tableRegion ? [tableRegion] : []}
              setRegions={(nextRegions) => {
                const next = Array.isArray(nextRegions) ? nextRegions : []
                setTableRegion(next[0] ?? null)
              }}
              availableRegions={availableGridRegions}
              selectionMode='single'
            />
          )}

          {viewMode === 'multiview' && availableGridRegions.length > 1 && (
            <RegionManager
              regions={activeGridRegions}
              setRegions={setGridRegions}
              availableRegions={availableGridRegions}
              selectionMode={canUseMultiRegion ? 'multiple' : 'single'}
            />
          )}

          {/* Refresh — Outlined Icon-Button */}
          <button
            type='button'
            onClick={handleRefresh}
            aria-label='Aktualisieren'
            className='inline-flex items-center justify-center h-9 w-9 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-background hover:border-primary transition-colors'
          >
            <RefreshIcon className='h-4 w-auto text-primary' />
          </button>

          <ColumnChooserPopover
            application={viewMode === 'table' ? TABLE_STORAGE_KEY_PREFIX : GRID_COLUMN_CHOOSER_APP}
            columns={viewMode === 'table'
              // Volle Liste: das Popover schreibt die komplette Order --
              // gefilterte IDs wuerden beim Sortieren ans Tabellenende rutschen.
              ? ALL_COLUMNS.map((col) => [col.id, col.header])
              : GRID_COLUMNS}
            viewOptions={{
              value: viewMode,
              onChange: setViewMode,
              options: [
                { value: 'table', label: 'Table View' },
                { value: 'multiview', label: 'Multiview' },
              ],
            }}
            onReset={viewMode === 'table'
              ? ((!isEmpty(hiddenColumnIds) || !!columnOrder || !isEmpty(columnSizing)) && (() => {
                setHiddenColumnIds([])
                setColumnOrder()
                setColumnSizing({})
              }))
              : ((!isEmpty(gridHiddenColumnIds) || !!gridColumnOrder || !isEmpty(gridColumnSizing)) && (() => {
                setGridHiddenColumnIds([])
                setGridColumnOrder(null)
                setGridColumnSizing({})
              }))}
          >{({ setIsOpen, isOpen }) =>
            <button
              type='button'
              onClick={() => setIsOpen(val => !val)}
              className={`relative inline-flex items-center gap-2 h-9 px-3 rounded-lg border text-sm font-medium transition-colors ${isOpen ? 'border-primary text-primary bg-secondary' : 'border-neutral-200 dark:border-neutral-700 text-textTitle bg-background hover:border-primary'}`}
            >
              <i className='bx bx-slider-alt text-[17px] leading-none' aria-hidden></i>
              Ansicht
              <i className={`bx bx-chevron-down text-base leading-none -ml-0.5 ${isOpen ? 'text-primary' : 'text-neutral-400'}`} aria-hidden></i>
            </button>}
          </ColumnChooserPopover>

          {/* Primär-CTA (Mint-Gradient) — Export (nur eigene Listen, nicht B2B) */}
          {!supplierPrefix && (
            <button
              type='button'
              onClick={handleExportButtonClick}
              disabled={!canExportList || isExportingList || !isFileLoaded}
              className='inline-flex items-center gap-2 h-9 px-4 rounded-lg bg-gradient-to-br from-primary to-main text-white dark:text-black text-sm font-semibold hover:brightness-105 active:scale-[.98] transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:brightness-100 disabled:active:scale-100'
            >
              {isExportingList ? (
                <i className='bx bx-loader-alt animate-spin text-base leading-none'></i>
              ) : (
                <ExportIcon className='h-4 w-auto' />
              )}
              Export
            </button>
          )}
        </div>
      </header>

      {/* px-4 = gleiche linke Kante wie Titel + Toolbar-Bar (header px-4) */}
      <div className='px-4 pb-2'>
        {/* Kein onChange={handleRefresh}: die Filters-Komponente laedt selbst
            korrekt nach (Pill-Edits via reconcile, Panel beim Schliessen).
            handleRefresh hier feuerte den ['data']-Invalidate synchron VOR dem
            Write -- Query noch enabled -> Refetch mit ALTEM Filterstand moeglich,
            plus Doppel-Invalidate beim Panel-Schliessen. */}
        <Filters application='wholesale_wizard' supplierMode={!!supplierPrefix} />
      </div>
    </>
  );

  return (
    <section className="overflow-hidden w-full h-full flex flex-col dark:text-white">
      {/* Einziger Scroller der Seite (vertikal + horizontal): die EDGE-Leiste
          (LayoutHeaderSlot) scrollt vertikal mit weg, Subheader/Footer bleiben
          per sticky left beim Quer-Scrollen stehen, die thead-Spaltennamen
          kleben per sticky top. Subheader VOR der View-Verzweigung: bleibt
          beim Umschalten gemountet, sonst schliesst sich das Popover. */}
      <div ref={tableScrollRef} style={{ overflowAnchor: 'none' }} className="flex-1 min-h-0 flex flex-col overflow-auto no-h-scrollbar">
      <LayoutHeaderSlot />
      <div className='sticky left-0 z-20 bg-background border-b border-gray-300 dark:border-neutral-700'>
        {subHeader}
      </div>
      {viewMode === 'table' ? (
        <>
            {(isFetching || pendingDataFilterWrites > 0) && (
              <div className="spinner spinner-relative">
                <i className="animate-spin bx bx-loader-alt text-4xl text-primary"></i>
              </div>
            )}
            {/* Tabelle DIREKT im Scroller (kein overflow-x-Wrapper): jeder
                Overflow-Wrapper dazwischen wuerde das sticky der thead
                einfangen und die Spaltennamen scrollten mit weg. */}
            <table className=' border-collapse productTable font-medium'>
              <thead className='sticky top-0 z-10'>
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header, at) => {
                      let resizeHandler = header.getResizeHandler()
                      return (
                        <th
                          key={header.id}
                          draggable={!header.column.getIsResizing() && at < columns.length - RIGHT_PINNED_COLUMNS ? true : undefined}
                          onDragStart={(e) => handleDragStart(e, header.id)}
                          onDragOver={handleDragOver}
                          onDrop={(e) => handleDrop(e, header.id)}
                          onDragEnter={(e) => handleDragEnter(e, header.id)}
                          onDragLeave={handleDragLeave}
                          className={classNames({
                            'dragOver': header.id === dragOverColumnId,
                          })}
                          style={header.column.getCanResize() ? {
                            minWidth: header.getSize(),
                            maxWidth: header.getSize(),
                          } : {
                            width: '1px',
                            maxWidth: 'fit-content',
                          }}
                        >
                          <div className='flex items-center justify-center gap-1'>
                            <button
                              onClick={() => {
                                // Sortierung in der Supplier-Ansicht deaktiviert
                                // (Sort auf berechnete Spalten = Filesort ueber ~1 Mio Rows).
                                if (SORT_COLUMNS.includes(header.id) && !supplierPrefix) {
                                  let sortBy = header.id
                                  let sortType = 'ASC'
                                  if (sort.sortBy === sortBy) {
                                    sortType = (
                                      sort.sortType === 'ASC' ? 'DESC' :
                                        sort.sortType === 'DESC' ? undefined :
                                          'ASC'
                                    )
                                  }
                                  setSort({ sortBy: sortType ? sortBy : undefined, sortType })
                                }
                              }}
                              className='inline-flex items-center gap-1'
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {!supplierPrefix && (sort.sortBy === header.id ? <>
                                {sort.sortType === 'ASC' ?
                                  <SortIcon className='d-inline w-3 h-3 rotate-180 text-primary' /> :
                                  <SortIcon className='w-3 h-3 text-primary' />
                                }
                              </> :
                                SORT_COLUMNS.includes(header.id) ? <SortIcon className='w-3 h-3 opacity-15 text-black dark:text-white' /> : null
                              )}
                            </button>

                          </div>
                          {header.column.getCanResize() &&
                            <div
                              onDoubleClick={() => header.column.resetSize()}
                              onMouseDown={resizeHandler}
                              onTouchStart={resizeHandler}
                              className={classNames('resizer', {
                                'resizing': header.column.getIsResizing(),
                              })}
                            />
                          }
                        </th>
                      )
                    })}
                    <th className='min-w-20 w-full'></th>
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => {
                  let rowKey = row.original?.asin || row.original?.ean || row.id
                  return (
                    <tr
                      key={row.id}
                      className={classNames({ selected: rowKey === selectedRowId })}
                      onClick={() => setSelectedRowId(rowKey)}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td
                          key={cell.id}
                          style={cell.column.getCanResize() ? {
                            maxWidth: cell.column.getSize(),
                            minWidth: cell.column.getSize(),
                          } : {}}
                        >{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                      <td />
                    </tr>
                  )
                })}
              </tbody>
            </table>
            <PaginationBar
              pageIndex={pagination.pageIndex}
              totalPages={displayTotalPages}
              hasNext={displayHasNext}
              setPage={handlePageChange}
              pageSize={pagination.pageSize}
              setPageSize={handlePageSizeChange}
              pageSizeOptions={[20, 50, 100]}
            />
        </>
      ) : viewMode === 'multiview' ? (
        <>
          <ProductGrid
            application={application}
            regions={activeGridRegions}
            search={search}
            hiddenColumnIds={gridHiddenColumnIds}
            setHiddenColumnIds={setGridHiddenColumnIds}
            listId={listId}
            favoriteId={favoriteId}
            supplierPrefix={supplierPrefix}
            storageKeyPrefix={GRID_STORAGE_KEY_PREFIX}
            columnSettingsModule={GRID_SETTINGS_MODULE}
            outerScroll
            onPageViewportReset={scrollPageToTop}
          />
        </>
      ) : null}
      </div>

      {/* Favorite Modal */}
      {selectedProduct &&
        <CommonModal
          isOpen={!!selectedProduct}
          closeModal={() => setSelectedProduct(null)}
        >
          <ProductFavorite
            product={selectedProduct}
            region={selectedProduct?.primaryRegion || (viewMode === 'table' ? tableRegion : activeGridRegions?.[0]) || data.region}
            onClose={() => setSelectedProduct(null)}
          />
        </CommonModal>
      }
    </section>
  )
}
